import type { ItemFieldRow } from './item-field-reader';
import { createAbilityEssenceSourceCollector } from './ability-essence-sources';
import { createAncestralEssenceSourceCollector } from './ancestral-essence-sources';
import { createEssenceDisplaySourceCollector } from './essence-display-sources';
import type { EssenceDisplayDependencies } from './essence-display-sources';
import { createComputeSourceCleaner } from './compute-source-cleanup';
import type { ComputeSourceCleanupDependencies } from './compute-source-cleanup';
import type { FormulaReadDependencies } from './formula-read-context';
import type { FormulaVariableMetadata } from './formula-variable-resolution';
import { createFormulaReadContext } from './formula-read-context';
import { scoreCarrierIntegerLayer } from './formula-variable-resolution';
import { integerFragmentBraces } from './status-integer-contribution';
import { phpJsonFloat, roundPhpDecimal } from './php-decimal-values';
import { phpJsonInteger, phpFloatToInteger } from './php-integer-arithmetic';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { integerCompileWorkingRow, integerCompileResult, putIntegerCompilePath } from './integer-compute-slots';
import { createFeatEssenceSourceCollector } from './feat-essence-sources';
import type { FeatEssenceSourceDependencies } from './feat-essence-sources';
import { createInventoryEssenceSourceCollector } from './inventory-essence-sources';

type Row = Record<string, unknown>;
const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, key: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, key) ? (v as Row)[key] : undefined;
const requireArray = (v: unknown): ItemFieldRow => { if (!arrayLike(v)) throw new TypeError('Essence rollup requires a PHP array'); return v; };
const stringArg = (v: unknown): string => { if (v == null || arrayLike(v)) throw new TypeError('Essence name/ID requires a scalar string'); return phpJsonString(v); };
const copy = <T>(v: T): T => arrayLike(v) ? integerCompileResult(integerCompileWorkingRow(v)) as T : v;
const int = (v: unknown): number => Number(phpJsonInteger(v));
const roundInt = (v: number): number => Number(phpFloatToInteger(roundPhpDecimal(v, 0)));
export type CreatureEssenceSource = 'collect_feat_choice_essence_contribution' | 'collect_feat_direct_integer_layer_contribution'
    | 'collect_inventory_essence_sources_for_display' | 'collect_ability_direct_integer_layer_contribution'
    | 'collect_ancestral_essence_sources_for_display' | 'collect_background_essence_sources_for_display'
    | 'collect_feat_essence_sources_for_display' | 'collect_ability_essence_sources_for_display' | 'lean-sources';
export interface CreatureEssenceRollupDependencies extends FormulaReadDependencies {
    metadata: FormulaVariableMetadata;
    /** Explicit unported collector inputs. Never default missing inputs to zero.
     * Their presence proves the orchestration, not those collectors' TS parity.
     */
    source: (kind: CreatureEssenceSource, args: readonly unknown[]) => ItemFieldRow;
}
/** Full rollup_essence/build_computed_essence_provenance orchestration, with the
 * actual source-reader order and one final write to computed.creature.essences.
 * Source collection/label resolution remains an explicit dependency boundary.
 */
export function createCreatureEssenceRollupCompiler(deps: CreatureEssenceRollupDependencies) {
    const readers = createFormulaReadContext(deps);
    const source = (kind: CreatureEssenceSource, args: readonly unknown[]): ItemFieldRow => {
        deps.trace?.(copy([kind, ...args])); return copy(requireArray(deps.source(kind, args)));
    };
    function rollup(input: ItemFieldRow, rawName: string, rawId: string, defaultBase = 0): ItemFieldRow {
        const host = integerCompileWorkingRow(requireArray(input)), name = stringArg(rawName), id = stringArg(rawId);
        if (defaultBase == null || arrayLike(defaultBase) || typeof defaultBase === 'string' && !/^\s*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?\s*$/i.test(defaultBase)) throw new TypeError('Essence default requires an integer');
        const base = scoreCarrierIntegerLayer(host, name, int(defaultBase), false, deps.metadata, readers);
        let feat = scoreCarrierIntegerLayer(host, 'feat_' + name, 0, false, deps.metadata, readers);
        feat += phpJsonFloat(own(source('collect_feat_choice_essence_contribution', [host, id]), 'rolled_total'));
        feat += phpJsonFloat(own(source('collect_feat_direct_integer_layer_contribution', [host, 'feat_' + name]), 'rolled_total'));
        const uncapped = name === 'spirit' || name === 'fortune', cap = Math.max(5, base), preCap = base + feat;
        const capped = uncapped ? preCap : Math.min(cap, preCap);
        const leaves: unknown[] = [];
        const flatten = (v: unknown): void => { if (arrayLike(v)) for (const child of Object.values(v)) flatten(child); else leaves.push(v); };
        flatten(readers.layerEntries(host, 'ability_' + name));
        const abilityLocked = integerFragmentBraces(leaves).length !== 0;
        const inventory = source('collect_inventory_essence_sources_for_display', [host, name]);
        const inventoryValue = phpJsonFloat(own(inventory, 'rolled_total')), inventoryLocked = phpJsonTruthy(own(inventory, 'locked'));
        let ability = scoreCarrierIntegerLayer(host, 'ability_' + name, 0, false, deps.metadata, readers);
        ability += phpJsonFloat(own(source('collect_ability_direct_integer_layer_contribution', [host, 'ability_' + name]), 'rolled_total'));
        const total = inventoryLocked && !abilityLocked ? inventoryValue : abilityLocked && !inventoryLocked ? ability
            : inventoryLocked && abilityLocked ? Math.max(inventoryValue, ability) : capped + inventoryValue + ability;
        const bundles = {
            ancestral: source('collect_ancestral_essence_sources_for_display', [host, name]),
            background: source('collect_background_essence_sources_for_display', [host, name]),
            feat: source('collect_feat_essence_sources_for_display', [host, name, id]),
            inventory: source('collect_inventory_essence_sources_for_display', [host, name]),
            ability: source('collect_ability_essence_sources_for_display', [host, name]),
        };
        const sources: Row[] = [];
        for (const [category, bundle] of Object.entries(bundles)) {
            for (const line of Object.values(requireArray(own(bundle, 'lines') ?? []))) sources.push({...requireArray(line), category});
        }
        const breakdown: Row = {default: roundInt(base)};
        for (const [category, bundle] of Object.entries(bundles)) breakdown[category] = int(own(bundle, 'rolled_total'));
        breakdown.other = 0; breakdown.override = 0;
        const pc = own(host, 'player_characters');
        if (arrayLike(pc)) {
            if (own(pc, name + '_input_default') != null) breakdown.default = int(own(pc, name + '_input_default'));
            breakdown.other = int(own(pc, name + '_input_other')); breakdown.override = int(own(pc, name + '_input_override'));
        }
        for (const [category, value] of Object.entries(breakdown)) if (category !== 'default' && value === 0) delete breakdown[category];
        const lean = source('lean-sources', [sources, host]);
        const entry: Row = {total: roundInt(total), base: roundInt(base), breakdown, locked: inventoryLocked || abilityLocked};
        if (Object.keys(lean).length) entry.sources = lean;
        const capMax = int(cap);
        if (!uncapped && preCap > capMax) entry.cap = {applied: true, max: capMax, pre_cap: roundInt(preCap)};
        putIntegerCompilePath(host, ['computed', 'creature', 'essences', name], entry);
        return integerCompileResult(host);
    }
    return rollup;
}

/** Replace both feat-result dependencies with actual source walks. Other
 * collectors remain explicit until their own source implementations are ported.
 */
export function createCreatureEssenceRollupWithFeatSources(deps: CreatureEssenceRollupDependencies & FeatEssenceSourceDependencies) {
    const feats = createFeatEssenceSourceCollector(deps);
    return createCreatureEssenceRollupCompiler({...deps, trace: deps.trace ? event => {
        if (event[0] !== 'collect_feat_choice_essence_contribution' && event[0] !== 'collect_feat_direct_integer_layer_contribution') deps.trace?.(event);
    } : undefined, source: (kind, args) => {
        if (kind === 'collect_feat_choice_essence_contribution') return feats.choice(args[0] as ItemFieldRow, args[1] as string);
        if (kind === 'collect_feat_direct_integer_layer_contribution') return feats.direct(args[0] as ItemFieldRow, args[1] as string);
        return deps.source(kind, args);
    }});
}

/** Inventory results now come from retained equipment/definition inputs too. */
export function createCreatureEssenceRollupWithFeatAndInventorySources(deps: CreatureEssenceRollupDependencies & FeatEssenceSourceDependencies) {
    const inventory = createInventoryEssenceSourceCollector(deps);
    return createCreatureEssenceRollupWithFeatSources({...deps, trace: deps.trace ? event => {
        if (event[0] !== 'collect_inventory_essence_sources_for_display') deps.trace?.(event);
    } : undefined, source: (kind, args) => kind === 'collect_inventory_essence_sources_for_display'
        ? inventory(args[0] as ItemFieldRow, args[1] as string) : deps.source(kind, args)});
}

/** Compose passive/status essence walks with feat and equipment walks. Ancestry,
 * background, feat display and lean source cleanup are still explicit inputs.
 */
export function createCreatureEssenceRollupWithAbilitySources(deps: CreatureEssenceRollupDependencies & FeatEssenceSourceDependencies) {
    const ability = createAbilityEssenceSourceCollector(deps);
    const kinds = new Set(['collect_ability_direct_integer_layer_contribution', 'collect_ability_essence_sources_for_display']);
    return createCreatureEssenceRollupWithFeatAndInventorySources({...deps, trace: deps.trace ? event => {
        if (!kinds.has(event[0] as string)) deps.trace?.(event);
    } : undefined, source: (kind, args) => {
        if (kind === 'collect_ability_direct_integer_layer_contribution') return ability.direct(args[0] as ItemFieldRow, args[1] as string);
        if (kind === 'collect_ability_essence_sources_for_display') return ability.display(args[0] as ItemFieldRow, args[1] as string);
        return deps.source(kind, args);
    }});
}

/** Ancestry provenance now comes from its actual passive/carrier walk. Its
 * numeric base still belongs to the earlier preliminary-essence stage.
 */
export function createCreatureEssenceRollupWithAncestralSources(deps: CreatureEssenceRollupDependencies & FeatEssenceSourceDependencies) {
    const ancestry = createAncestralEssenceSourceCollector(deps);
    return createCreatureEssenceRollupWithAbilitySources({...deps, trace: deps.trace ? event => {
        if (event[0] !== 'collect_ancestral_essence_sources_for_display') deps.trace?.(event);
    } : undefined, source: (kind, args) => kind === 'collect_ancestral_essence_sources_for_display'
        ? ancestry.display(args[0] as ItemFieldRow, args[1] as string) : deps.source(kind, args)});
}

/** All five provenance categories now walk source inputs; lean cleanup remains supplied. */
export function createCreatureEssenceRollupWithDisplaySources(deps: CreatureEssenceRollupDependencies & EssenceDisplayDependencies) {
    const display = createEssenceDisplaySourceCollector(deps);
    const kinds = new Set(['collect_background_essence_sources_for_display', 'collect_feat_essence_sources_for_display']);
    return createCreatureEssenceRollupWithAncestralSources({...deps, trace: deps.trace ? event => {
        if (!kinds.has(event[0] as string)) deps.trace?.(event);
    } : undefined, source: (kind, args) => {
        if (kind === 'collect_background_essence_sources_for_display') return display.background(args[0] as ItemFieldRow, args[1] as string);
        if (kind === 'collect_feat_essence_sources_for_display') return display.feat(args[0] as ItemFieldRow, args[1] as string, args[2] as string);
        return deps.source(kind, args);
    }});
}

/** Complete essence rollup source composition, including identity-based elision. */
export function createCreatureEssenceRollupWithSourceCleanup(deps: CreatureEssenceRollupDependencies & EssenceDisplayDependencies & ComputeSourceCleanupDependencies, cleaner = createComputeSourceCleaner(deps)) {
    return createCreatureEssenceRollupWithDisplaySources({...deps, trace: deps.trace ? event => {
        if (event[0] !== 'lean-sources') deps.trace?.(event);
    } : undefined, source: (kind, args) => kind === 'lean-sources'
        ? cleaner.clean(args[0] as ItemFieldRow, args[1] as ItemFieldRow) : deps.source(kind, args)});
}
